import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { authChildGuard } from './guards/auth.guard';
import { loginGuard } from './guards/login.guard';
import { createUserRoleGuard } from './guards/user-role.guard';
import { tenantGuard } from './guards/tenant.guard';

import { BaseLayoutComponent } from '@components/shared/base-layout/base-layout.component';
import { DeviceListComponent } from '@components/device-list/device-list.component';
import { DeviceSettingListComponent } from '@components/device-setting-list/device-setting-list.component';
import { AccountListComponent } from '@components/account-list/account-list.component';
import { TenantListComponent } from '@components/tenant-list/tenant-list.component';
import { CenterCardLayoutComponent } from '@components/shared/center-card-layout/center-card-layout.component';
import { loggedInUserResolver } from './resolvers/logged-in-user.resolver';
import { DeviceTypeListComponent } from '@components/device-type-list/device-type-list.component';
import { TenantSelectComponent } from '@components/tenant-select/tenant-select.component';
import { AssetTypeListComponent } from '@components/asset-type-list/asset-type-list.component';
import { ErrorComponent } from '@components/error/error.component';
import { SimpleLayoutComponent } from '@components/shared/simple-layout/simple-layout.component';
import { DashboardComponent } from '@components/dashboard/dashboard.component';
import { DeviceDetailComponent } from '@components/device-info/device-detail/device-detail.component';
import { DeviceInfoListComponent } from '@components/device-info/device-info-list/device-info-list.component';
import { NotificationListComponent } from '@components/notification-list/notification-list.component';
import { TenantSettingComponent } from '@components/tenant-setting/tenant-setting.component';
import { LogListComponent } from '@components/log-list/log-list.component';
import { ShipmentLocationListComponent } from '@components/shipment-location-list/shipment-location-list.component';
import { ShipmentListComponent } from '@components/shipment-list/shipment-list.component';
import { ShipmentInfoComponent } from '@components/shipment-info/shipment-info.component';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [loginGuard],
    component: CenterCardLayoutComponent,
    loadChildren: () =>
      import('./components/login/login.routes').then((m) => m.LoginModule),
  },
  {
    path: 'password-reset',
    component: CenterCardLayoutComponent,
    loadChildren: () =>
      import('./components/password-reset/password-reset.routes').then(
        (m) => m.PasswordResetModule,
      ),
  },
  {
    path: '',
    component: BaseLayoutComponent,
    canActivate: [authChildGuard],
    resolve: {
      loggedInUser: loggedInUserResolver,
    },
    children: [
      // 共通
      {
        path: 'accounts',
        resolve: {
          loggedInUser: loggedInUserResolver,
        },
        component: AccountListComponent,
        canActivate: [createUserRoleGuard(['system_admin', 'tenant_admin'])],
      },

      // テナントメニュー（システム管理者がアクセスできる場合、tenantGuardを付けること）
      {
        path: 'dashboard',
        component: DashboardComponent,
        data: { reuse: true },
        canActivate: [
          createUserRoleGuard([
            'system_admin',
            'tenant_admin',
            'tenant_editor',
            'tenant_viewer',
          ]),
          tenantGuard,
        ],
        resolve: {
          loggedInUser: loggedInUserResolver,
        },
      },
      {
        path: 'device-info',
        resolve: {
          loggedInUser: loggedInUserResolver,
        },
        children: [
          {
            path: '',
            component: DeviceInfoListComponent,
            pathMatch: 'full',
            data: { reuse: true },
            canActivate: [
              createUserRoleGuard([
                'system_admin',
                'tenant_admin',
                'tenant_editor',
                'tenant_viewer',
              ]),
              tenantGuard,
            ],
          },
          {
            path: ':deviceId',
            component: DeviceDetailComponent,
            canActivate: [
              createUserRoleGuard([
                'system_admin',
                'tenant_admin',
                'tenant_editor',
                'tenant_viewer',
              ]),
              tenantGuard,
            ],
            resolve: {
              loggedInUser: loggedInUserResolver,
            },
          },
        ],
      },
      {
        path: 'device-settings',
        resolve: {
          loggedInUser: loggedInUserResolver,
        },
        component: DeviceSettingListComponent,
        canActivate: [createUserRoleGuard(['tenant_admin', 'tenant_editor'])],
      },
      {
        path: 'setting-templates',
        resolve: {
          loggedInUser: loggedInUserResolver,
        },
        component: AssetTypeListComponent,
        canActivate: [
          createUserRoleGuard([
            'system_admin',
            'tenant_admin',
            'tenant_editor',
          ]),
          tenantGuard,
        ],
      },
      {
        path: 'shipment-info',
        resolve: {
          loggedInUser: loggedInUserResolver,
        },
        component: ShipmentInfoComponent,
        canActivate: [
          createUserRoleGuard([
            'system_admin',
            'tenant_admin',
            'tenant_editor',
            'tenant_viewer',
          ]),
          tenantGuard,
        ],
      },
      {
        path: 'shipments',
        children: [
          { path: '', pathMatch: 'full', component: ShipmentListComponent },
          { path: 'locations', component: ShipmentLocationListComponent },
        ],
        canActivate: [createUserRoleGuard(['tenant_admin', 'tenant_editor'])],
      },
      {
        path: 'audit-logs',
        resolve: {
          loggedInUser: loggedInUserResolver,
        },
        component: LogListComponent,
        canActivate: [createUserRoleGuard(['tenant_admin'])],
      },
      {
        path: 'tenant-settings',
        component: TenantSettingComponent,
        canActivate: [createUserRoleGuard(['tenant_admin'])],
      },

      // システム管理者メニュー
      {
        path: 'devices',
        component: DeviceListComponent,
        canActivate: [createUserRoleGuard(['system_admin'])],
      },
      {
        path: 'device-types',
        component: DeviceTypeListComponent,
        canActivate: [createUserRoleGuard(['system_admin'])],
      },
      {
        path: 'notifications',
        component: NotificationListComponent,
        canActivate: [createUserRoleGuard(['system_admin'])],
      },
      {
        path: 'tenants',
        component: TenantListComponent,
        canActivate: [createUserRoleGuard(['system_admin'])],
      },

      // 権限に沿った正しい遷移をするように一度ログイン画面に遷移させる
      { path: '', pathMatch: 'full', redirectTo: '/dashboard' },
    ],
  },
  {
    path: '',
    component: SimpleLayoutComponent,
    canActivate: [authChildGuard],
    children: [
      {
        path: 'tenant-select',
        component: TenantSelectComponent,
      },
      {
        path: 'error',
        component: ErrorComponent,
      },

      // 権限に沿った正しい遷移をするように一度ログイン画面に遷移させる
      { path: '', pathMatch: 'full', redirectTo: '/dashboard' },
    ],
  },
  { path: '**', redirectTo: '/login' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
